import { __awaiter, __generator, __read, __values } from "tslib";
import { AutoMonotonicMigration } from '@tencent/memoire-migration';
import { InternalEvent, StorageEvent } from '../event';
import { TableOperationUtils } from '../model';
import { SystemTableHelper } from '../utils/system-table-helper';
import { MigrationActionExecutor } from './action-executor';
import { LazyDataMigrationProcessor } from './lazy-data-migration-processor';
import { DATABASE_DEFAULT_CURRENT_VERSION, DATABASE_DEFAULT_OPEN_VERSION } from '../utils/constant';
import { ExecuteDataUpgradeError, UpdateMigrationChangeInfoError } from '../error';
var MigrationManager = /** @class */ (function () {
    function MigrationManager(eventBus, schemaProvider, aspects) {
        var _this = this;
        this.planner = new AutoMonotonicMigration();
        this.actualDBSchema = null;
        this.actualDBSchemaFrom = null;
        this.lazyMigrationProcessor = new LazyDataMigrationProcessor(this);
        // this function will use in event listener,should bind this
        this.handleCollectionCreated = function (_a) {
            var collectionName = _a.collectionName, context = _a.context;
            _this.safeExecuteDataUpgrade(context, collectionName);
        };
        // this function will use in event listener,should bind this
        this.handleStartTransaction = function (_a) {
            var e_1, _b;
            var collectionNames = _a.collectionNames, context = _a.context;
            try {
                for (var collectionNames_1 = __values(collectionNames), collectionNames_1_1 = collectionNames_1.next(); !collectionNames_1_1.done; collectionNames_1_1 = collectionNames_1.next()) {
                    var collectionName = collectionNames_1_1.value;
                    _this.safeExecuteDataUpgrade(context, collectionName);
                }
            }
            catch (e_1_1) { e_1 = { error: e_1_1 }; }
            finally {
                try {
                    if (collectionNames_1_1 && !collectionNames_1_1.done && (_b = collectionNames_1.return)) _b.call(collectionNames_1);
                }
                finally { if (e_1) throw e_1.error; }
            }
        };
        this.eventBus = eventBus;
        this.eventBus.on(InternalEvent.CollectionCreated, this.handleCollectionCreated);
        this.eventBus.on(InternalEvent.StartTransaction, this.handleStartTransaction);
        this.schemaProvider = schemaProvider;
        this.actionExecutor = new MigrationActionExecutor(aspects.actionExecutor);
        this.applyAspectPatch(aspects);
    }
    MigrationManager.prototype.use = function (planner) {
        this.planner = planner;
    };
    MigrationManager.prototype.prepareAutoUpgradeHandler = function (dbName, engine) {
        return __awaiter(this, void 0, void 0, function () {
            var expectedDBSchema, _a, schema, from, migrations;
            var _this = this;
            return __generator(this, function (_b) {
                switch (_b.label) {
                    case 0:
                        expectedDBSchema = this.schemaProvider.getDatabaseSchema();
                        return [4 /*yield*/, SystemTableHelper.getDatabaseSchema(dbName, engine, expectedDBSchema)];
                    case 1:
                        _a = _b.sent(), schema = _a.schema, from = _a.from;
                        this.actualDBSchema = schema;
                        this.actualDBSchemaFrom = from;
                        this.log('getDatabaseSchema', this.actualDBSchema);
                        migrations = this.buildMigration({
                            schema: {
                                expect: expectedDBSchema,
                                actual: this.actualDBSchema,
                            },
                        });
                        return [2 /*return*/, function (database, oldVersion, newVersion, transaction) {
                                _this.eventBus.emit(StorageEvent.SchemaMigrationStart);
                                _this.executeSchemaMigration(database, migrations, transaction);
                                _this.updateMigrationChangeInfo(transaction).catch(function (err) {
                                    _this.emitError(new UpdateMigrationChangeInfoError(err));
                                });
                                _this.eventBus.emit(StorageEvent.SchemaMigrationEnd);
                            }];
                }
            });
        });
    };
    // buildMigration() use the planner to make migration decision.
    // the decision are made of migration actions, which will be performed one by one.
    MigrationManager.prototype.buildMigration = function (state) {
        var _a, _b, _c, _d, _e, _f;
        var _g = state.schema, actual = _g.actual, expect = _g.expect;
        (_b = (_a = this.planner) === null || _a === void 0 ? void 0 : _a.init) === null || _b === void 0 ? void 0 : _b.call(_a, state);
        var dbOriginVersion = (_c = actual.version) !== null && _c !== void 0 ? _c : DATABASE_DEFAULT_CURRENT_VERSION;
        var openVersion = (_d = expect.version) !== null && _d !== void 0 ? _d : DATABASE_DEFAULT_OPEN_VERSION;
        var migrations = (_f = (_e = this.planner) === null || _e === void 0 ? void 0 : _e.plan(dbOriginVersion, openVersion)) !== null && _f !== void 0 ? _f : [];
        return migrations;
    };
    MigrationManager.prototype.executeSchemaMigration = function (db, migrations, transaction) {
        var e_2, _a, e_3, _b;
        if (!migrations) {
            return;
        }
        try {
            for (var migrations_1 = __values(migrations), migrations_1_1 = migrations_1.next(); !migrations_1_1.done; migrations_1_1 = migrations_1.next()) {
                var migration = migrations_1_1.value;
                try {
                    for (var _c = (e_3 = void 0, __values(migration.schema.actions)), _d = _c.next(); !_d.done; _d = _c.next()) {
                        var action = _d.value;
                        this.actionExecutor.executeSchemaAction(db, transaction, action);
                    }
                }
                catch (e_3_1) { e_3 = { error: e_3_1 }; }
                finally {
                    try {
                        if (_d && !_d.done && (_b = _c.return)) _b.call(_c);
                    }
                    finally { if (e_3) throw e_3.error; }
                }
            }
        }
        catch (e_2_1) { e_2 = { error: e_2_1 }; }
        finally {
            try {
                if (migrations_1_1 && !migrations_1_1.done && (_a = migrations_1.return)) _a.call(migrations_1);
            }
            finally { if (e_2) throw e_2.error; }
        }
    };
    MigrationManager.prototype.executeDataMigration = function (db, migrations, transaction) {
        return __awaiter(this, void 0, void 0, function () {
            var migrations_2, migrations_2_1, migration, _a, _b, action, e_4_1, e_5_1;
            var e_5, _c, e_4, _d;
            return __generator(this, function (_e) {
                switch (_e.label) {
                    case 0:
                        if (!migrations) {
                            return [2 /*return*/];
                        }
                        _e.label = 1;
                    case 1:
                        _e.trys.push([1, 12, 13, 14]);
                        migrations_2 = __values(migrations), migrations_2_1 = migrations_2.next();
                        _e.label = 2;
                    case 2:
                        if (!!migrations_2_1.done) return [3 /*break*/, 11];
                        migration = migrations_2_1.value;
                        _e.label = 3;
                    case 3:
                        _e.trys.push([3, 8, 9, 10]);
                        _a = (e_4 = void 0, __values(migration.data.actions)), _b = _a.next();
                        _e.label = 4;
                    case 4:
                        if (!!_b.done) return [3 /*break*/, 7];
                        action = _b.value;
                        return [4 /*yield*/, this.actionExecutor.executeDataAction(db, transaction, action)];
                    case 5:
                        _e.sent();
                        _e.label = 6;
                    case 6:
                        _b = _a.next();
                        return [3 /*break*/, 4];
                    case 7: return [3 /*break*/, 10];
                    case 8:
                        e_4_1 = _e.sent();
                        e_4 = { error: e_4_1 };
                        return [3 /*break*/, 10];
                    case 9:
                        try {
                            if (_b && !_b.done && (_d = _a.return)) _d.call(_a);
                        }
                        finally { if (e_4) throw e_4.error; }
                        return [7 /*endfinally*/];
                    case 10:
                        migrations_2_1 = migrations_2.next();
                        return [3 /*break*/, 2];
                    case 11: return [3 /*break*/, 14];
                    case 12:
                        e_5_1 = _e.sent();
                        e_5 = { error: e_5_1 };
                        return [3 /*break*/, 14];
                    case 13:
                        try {
                            if (migrations_2_1 && !migrations_2_1.done && (_c = migrations_2.return)) _c.call(migrations_2);
                        }
                        finally { if (e_5) throw e_5.error; }
                        return [7 /*endfinally*/];
                    case 14: return [2 /*return*/];
                }
            });
        });
    };
    MigrationManager.prototype.updateMigrationChangeInfo = function (transaction) {
        var _a, _b;
        return __awaiter(this, void 0, void 0, function () {
            var schema, dbOpenVersion;
            return __generator(this, function (_c) {
                schema = this.schemaProvider.getDatabaseSchema();
                dbOpenVersion = (_a = schema.version) !== null && _a !== void 0 ? _a : DATABASE_DEFAULT_OPEN_VERSION;
                return [2 /*return*/, SystemTableHelper.updateSchemaChange(dbOpenVersion, transaction, {
                        schema: {
                            expect: schema,
                            actual: (_b = this.actualDBSchema) !== null && _b !== void 0 ? _b : { name: schema.name, collections: [] },
                        },
                    }, this.actualDBSchemaFrom === "scan_exists_table" /* SCAN_EXISTING_TABLE */)];
            });
        });
    };
    /**
     * this function is only the stub for the logger plugin, real log logic is in plugin.
     */
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    MigrationManager.prototype.log = function (action) {
        var args = [];
        for (var _i = 1; _i < arguments.length; _i++) {
            args[_i - 1] = arguments[_i];
        }
    };
    MigrationManager.prototype.executeDataUpgradeIfNeed = function (modelClass, db) {
        var _a;
        return __awaiter(this, void 0, void 0, function () {
            var schema, defaultSchema;
            var _this = this;
            return __generator(this, function (_b) {
                switch (_b.label) {
                    case 0:
                        this.log('executeDataUpgradeIfNeed', modelClass.name);
                        schema = this.schemaProvider.getDatabaseSchema();
                        defaultSchema = { name: schema.name, collections: [] };
                        return [4 /*yield*/, this.lazyMigrationProcessor.executeDataUpgrade(modelClass, db, {
                                schema: {
                                    expect: schema,
                                    actual: (_a = this.actualDBSchema) !== null && _a !== void 0 ? _a : defaultSchema,
                                },
                            }, function () {
                                _this.eventBus.emit(InternalEvent.ModelLazyMigrationEnd, TableOperationUtils.getTableName(modelClass));
                            })];
                    case 1:
                        _b.sent();
                        return [2 /*return*/];
                }
            });
        });
    };
    MigrationManager.prototype.safeExecuteDataUpgrade = function (context, collectionName) {
        var _this = this;
        Promise.all([context.waitForModelRegistered(collectionName), context.waitForDBReady()])
            .then(function (_a) {
            var _b = __read(_a, 2), model = _b[0], db = _b[1];
            _this.executeDataUpgradeIfNeed(model.class, db).catch(function (err) {
                _this.emitError(new ExecuteDataUpgradeError(collectionName, err));
            });
        })
            .catch(function () {
            // it means the db is closed, safely skip data upgrade
        });
    };
    MigrationManager.prototype.emitError = function (err) {
        this.eventBus.emit(StorageEvent.Error, err);
    };
    MigrationManager.prototype.applyAspectPatch = function (aspects) {
        aspects.buildMigration.patch(this, 'buildMigration');
        aspects.executeDataMigration.patch(this, 'executeDataMigration');
        aspects.executeSchemaMigration.patch(this, 'executeSchemaMigration');
        aspects.prepareAutoUpgradeHandler.patch(this, 'prepareAutoUpgradeHandler');
        aspects.log.patch(this, 'log');
        aspects.updateMigrationChangeInfo.patch(this, 'updateMigrationChangeInfo');
    };
    return MigrationManager;
}());
export default MigrationManager;
//# sourceMappingURL=migration-manager.js.map